import { useEffect, useState } from 'react'
import { usePanel } from '../lib/panelContext'

async function copyText(value: string): Promise<boolean> {
  const text = (value || '').trim()
  if (!text) return false
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

type PipelineModel = {
  role: string
  model: string
  provider: string
  runs: string
  status: string
  percent: number | null
}

export default function Settings() {
  const {
    backendUrl,
    setBackendUrl,
    panel,
    panelError,
    refreshPanel,
    connected,
    connecting,
  } = usePanel()
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [pipeline, setPipeline] = useState<PipelineModel[]>([])

  useEffect(() => {
    let stop = false
    const load = async () => {
      try {
        const res = await fetch('/api/models/pipeline')
        if (res.ok && !stop) setPipeline(((await res.json()) as { models: PipelineModel[] }).models)
      } catch {
        /* backend offline: leave the list empty */
      }
    }
    void load()
    const timer = window.setInterval(load, 5000)
    return () => {
      stop = true
      window.clearInterval(timer)
    }
  }, [])

  return (
    <div className="page-body">
      <header className="page-head">
        <h1>Settings</h1>
        <p>Models in use, backend URL and LiveKit credentials for workers.</p>
      </header>

      <section className="panel-section" data-testid="model-list">
        <h2>Models</h2>
        {pipeline.length === 0 ? (
          <p>Models unavailable — backend not reachable.</p>
        ) : (
          <table className="model-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Model</th>
                <th>Provider</th>
                <th>Runs</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pipeline.map((m) => (
                <tr key={m.role}>
                  <td>{m.role}</td>
                  <td><code className="mono">{m.model}</code></td>
                  <td>{m.provider}</td>
                  <td>{m.runs}</td>
                  <td>
                    {m.status}
                    {m.status === 'downloading' && m.percent !== null ? ` · ${m.percent}%` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel-section">
        <h2>Backend</h2>
        <label className="panel-field">
          <span>URL (blank = Vite proxy)</span>
          <input
            value={backendUrl}
            onChange={(e) => setBackendUrl(e.target.value)}
            placeholder="(proxied /api → :8801)"
            disabled={connected || connecting}
          />
        </label>
        <button
          type="button"
          className="panel-ghost"
          onClick={() => {
            void refreshPanel()
          }}
        >
          Refresh
        </button>
        {panel ? (
          <ul className="panel-meta">
            <li>
              Status:{' '}
              <strong data-testid="backend-status">{panel.backend.status}</strong>
            </li>
            <li>LiveKit: {panel.backend.livekit}</li>
            <li className="panel-cred-label">Local LiveKit server</li>
            {(
              [
                ['url', 'URL', panel.backend.livekit_url],
                ['key', 'API key', panel.backend.livekit_api_key],
                ['secret', 'API secret', panel.backend.livekit_api_secret],
              ] as const
            ).map(([id, label, value]) => (
              <li key={id} className="panel-cred" data-testid={`livekit-${id}`}>
                <span className="panel-cred-name">{label}</span>
                <code className="mono">{value || '—'}</code>
                <button
                  type="button"
                  className="panel-copy"
                  disabled={!value}
                  onClick={() => {
                    void copyText(value || '').then((ok) => {
                      if (ok) {
                        setCopiedField(id)
                        window.setTimeout(() => setCopiedField(null), 1500)
                      }
                    })
                  }}
                >
                  {copiedField === id ? 'Copied' : 'Copy'}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="panel-hint">{panelError || 'Loading…'}</p>
        )}
      </section>

    </div>
  )
}
